<template>
  <asc-page-loading-indicator
    :loading="isDataLoading && apiPageFailure.length === 0"
  >
    <v-container fluid class="asc-report-details-container">
      <asc-report-header-template
        :report-id="reportId"
        :tail-number="tailNumber"
        :report-data="reportDataWithUniqueIds"
        :report-data-loading="isReportDataLoading"
        :timeline-data="reportTimeLinesData"
        :on-report-time-line-change="handleReportTimeLineChange"
        :on-previous-report="navToPreviousReport"
        :previous-disabled="previousButtonDisabled"
        :on-next-report="navToNextReport"
        :next-disabled="nextButtonDisabled"
      ></asc-report-header-template>
      <asc-container>
        <template v-if="getPlotsFeatureFlag && plotTabVisible" slot="tabs">
          <asc-tab v-model="reportsTabIndex" @tabChange="handleTabChange">
            <asc-tab-title title="Report" />
            <asc-tab-title title="Plots" />
          </asc-tab>
        </template>
        <asc-report-details-template
          :report-id="reportId"
          :aircraft-details="aircraftDetails"
          :aircraft-details-loading="isAircraftDetailsLoading"
          :fault-monitors="getGroupedFaultMonitors"
          :on-export-report="handleExportReport"
          :on-report-menu-click="handleParameterValueClick"
          :on-report-alert-click="handleReportAlertClick"
          :on-show-raw-report="handleShowRawReport"
          :report-alerts="reportAlerts"
          :is-apu-report="isApuReport"
          :report-data="reportDataWithUniqueIds"
          :report-data-loading="isReportDataLoading"
          :export-button-visible="isExporter"
          :available-plots="getPlotsFeatureFlag && plotTabVisible"
          :page-plots="pagePlots"
          :loading-plot-ids="loadingPlotIds"
          :loading-page-plots="loadingPagePlots"
          :chart-data="chartData"
          :internal-user="isInternalUser"
          :graph-groups="getCurrentGraphGroups"
          :on-expand="handleExpand"
          :on-update-tab-view-page-plot="updateTabView"
          :plot-group-date-range="dateRangeSelected"
          :graph-group-selected="graphGroupSelected"
          :tab-index="reportsTabIndex"
          @graphGroupChange="handleUpdateGraphGroup"
          @onDateChange="handleUpdateDateRange"
          @onTabChange="handleTabChange"
        ></asc-report-details-template>
      </asc-container>

      <asc-parameter-definition-dialog
        :loading="isParameterDefinitionLoading"
        :definition="parameterDefinition"
        :show-dialog="parameterDefinitionDialogVisible"
        :on-close="handleCloseDialog"
      ></asc-parameter-definition-dialog>

      <asc-parameter-alert-dialog
        :definition="parameterAlert"
        :show-dialog="parameterAlertDialogVisible"
        :loading="isParameterAlertLoading"
        :on-close="handleCloseDialog"
      ></asc-parameter-alert-dialog>

      <asc-fault-monitor-alert-dialog
        :definition="definition"
        :show-dialog="faultMonitorAlertDialogVisible"
        :loading="isFaultMonitorAlertLoading"
        :on-close="handleCloseDialog"
      ></asc-fault-monitor-alert-dialog>

      <asc-parameter-graph-dialog
        :loading="isParameterGraphLoading"
        :definition="parameterGraph"
        :show-dialog="parameterGraphDialogVisible"
        :on-close="handleCloseDialog"
        :on-date-change="handleParameterGraphDateChange"
        :dates="parameterGraphDates"
        :is-series-data="isSeriesData"
      ></asc-parameter-graph-dialog>

      <asc-fleet-graph-dialog
        :loading="isFleetGraphLoading"
        :definition="fleetGraphData"
        :show-dialog="fleetGraphDialogVisible"
        :on-close="handleCloseDialog"
        :on-date-change="handleFleetGraphDateChange"
        :dates="fleetGraphDates"
      ></asc-fleet-graph-dialog>

      <asc-raw-report-dialog
        :show-dialog="rawReportDialogVisible"
        :loading="isRawReportTextLoading"
        :on-close="handleCloseDialog"
        :report-text="rawReportText"
      ></asc-raw-report-dialog>

      <asc-report-menu
        :on-fault-monitor-alert-click="handleFaultMonitorAlertClick"
        :on-menu-close="handleCloseMenu"
        :on-parameter-alert-click="handleParameterAlertDialogClick"
        :on-parameter-definition-click="handleParameterDefinitionDialogClick"
        :on-parameter-graph-click="handleParameterGraphDialogClick"
        :on-fleet-graph-click="handleFleetGraphDialogClick"
        :plottable="clickedParameterIsPlottable"
        :alert-enabled="clickedParameterAlertEnabled"
        :show-menu="reportMenuDialogVisible"
        :status="getReportStatus"
        :fault-monitors="getReportCellFaultMonitors"
        :x="x"
        :y="y"
      ></asc-report-menu>

      <asc-loading-indicator-dialog
        text="Exporting"
        :show-dialog="isReportExportLoading"
        :loading="true"
      ></asc-loading-indicator-dialog>

      <asc-page-plot-dialog
        :show-dialog="pagePlotDialogVisible"
        :loading="loadingPlotDialogData"
        :page-plot="expandedPagePlot"
        :loading-plot-ids="loadingPlotIds"
        :chart-data="chartData"
        :filter-lookups="filterLookups"
        :editable="false"
        :on-close="handleClosePlotDialog"
        :on-accept="handlePagePlotDialog"
        :on-apply-page-plot="handleRefreshData"
        :on-update-tab-view-page-plot="updateTabView"
        :readonly="true"
      ></asc-page-plot-dialog>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import { AscReportDetailsTemplate } from '@/components/templates'
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscFaultMonitorAlertDialog from '@/components/molecules/AscFaultMonitorAlertDialog'
import AscParameterAlertDialog from '@/components/molecules/AscParameterAlertDialog'
import AscParameterDefinitionDialog from '@/components/molecules/AscParameterDefinitionDialog'
import AscParameterGraphDialog from '@/components/molecules/AscParameterGraphDialog'
import AscFleetGraphDialog from '@/components/molecules/AscFleetGraphDialog'
import AscRawReportDialog from '@/components/molecules/AscRawReportDialog'
import AscReportMenu from '@/components/molecules/AscReportMenu'
import AscLoadingIndicatorDialog from '@/components/molecules/AscLoadingIndicatorDialog'
import { mapState, mapGetters, mapActions } from 'vuex'
import {
  ACMF_REPORT_DETAILS_CONSTS as ARVC,
  ACMF_REPORT_LIST_CONSTS as ARLC,
  USER_CONSTS as UC,
  LOOKUP_CONSTS as LC,
} from '@/stores/consts'
import { AscReportNavMixin, AscReportDetailsMixin } from '../mixins'
import { AscPlottingMixin, AscAnalyticPlotsMixin } from '@/components/mixins'
import AscPlotDataMixin from '@/components/features/flightData/containers/AscPlotDataMixin'
import AscPlotFiltersMixin from '@/components/features/flightData/containers/AscPlotFiltersMixin'
import {
  getDateGivenOffset,
  getFormattedAPIDate,
  getStartDateGivenEndDateAndTimeline,
} from '@/utilities/dates'
import AscPagePlotDialog from '@/components/molecules/AscPagePlotDialog'
import { PLOT_UPDATE_MESSAGES } from '@/utilities/appConsts'
import AppFeatureFlags from '@/utilities/appFeatureFlags'
import AscContainer from '@/components/atoms/AscContainer'
import AscTab from '@/components/atoms/AscTab'
import AscTabTitle from '@/components/atoms/AscTabTitle'
import AscReportHeaderTemplate from '@/components/templates/AscReportHeaderTemplate'

export default {
  components: {
    AscPageLoadingIndicator,
    AscReportDetailsTemplate,
    AscFaultMonitorAlertDialog,
    AscParameterAlertDialog,
    AscParameterDefinitionDialog,
    AscParameterGraphDialog,
    AscFleetGraphDialog,
    AscRawReportDialog,
    AscReportMenu,
    AscLoadingIndicatorDialog,
    AscPagePlotDialog,
    AscContainer,
    AscTab,
    AscTabTitle,
    AscReportHeaderTemplate,
  },

  mixins: [
    AscReportNavMixin,
    AscReportDetailsMixin,
    AscPlottingMixin,
    AscPlotDataMixin,
    AscAnalyticPlotsMixin,
    AscPlotFiltersMixin,
  ],

  props: {
    reportId: {
      type: Number,
      required: true,
    },
    tailNumber: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      clickedParameter: null,
      definition: {},
      faultMonitorAlertDialogVisible: false,
      fleetGraphDialogVisible: false,
      isFaultMonitorAlertLoading: false,
      isSeriesData: false,
      parameterAlertDialogVisible: false,
      parameterDefinitionDialogVisible: false,
      parameterGraphDialogVisible: false,
      rawReportDialogVisible: false,
      reportMenuDialogVisible: false,
      x: null,
      y: null,
      parameterGraphDates: {
        dates: {
          startDate: getDateGivenOffset(new Date(), -30),
          endDate: new Date(),
          value: '4',
        },
      },
      fleetGraphDates: {
        dates: {
          startDate: getDateGivenOffset(new Date(), -30),
          endDate: new Date(),
          value: '4',
        },
      },
      pagePlotDialogVisible: false,
      expandedPagePlot: null,
      loadingPagePlots: false,
      dateRangeSelected: 1,
      graphGroupSelected: null,
      reportsTabIndex: this.getTabIndexFromLocalStorage(),
      pageNavigation: false,
      currentReportDate: null,
      loadingPlotDialogData: false,
    }
  },

  computed: {
    ...mapState(ARVC.NAMESPACE, [
      'reportAlerts',
      'aircraftDetails',
      'rawReportText',
      'parameterAlert',
      'parameterDefinition',
      'parameterGraph',
      'faultMonitors',
      'previousReportId',
      'nextReportId',
      'isParameterAlertLoading',
      'isReportDataLoading',
      'isAircraftDetailsLoading',
      'isParameterDefinitionLoading',
      'isParameterGraphLoading',
      'isFleetGraphLoading',
      'fleetGraphData',
      'isRawReportTextLoading',
      'isReportExportLoading',
      'reportTimeLinesData',
      'isReportTimeLinesDataLoading',
      'graphGroupsPerReportType',
      'reportType',
      'reportPlotGraphGroups',
      'reportPlotGraph',
    ]),
    ...mapGetters(ARVC.NAMESPACE, ['isApuReport', 'reportDataWithUniqueIds']),
    ...mapGetters(UC.NAMESPACE, ['isExporter', 'isInternalUser']),
    ...mapGetters(ARLC.NAMESPACE, [
      'mappedAcmfAirlines',
      'mappedAcmfMasterModels',
      'mappedAcmfTails',
      'mappedAcmfSystems',
      'mappedAcmfReportTypes',
      'mappedAcmfParameterNames',
    ]),
    ...mapState({ apiPageFailure: 'apiPageFailure' }),

    previousButtonDisabled() {
      return this.previousReportId === null
    },

    nextButtonDisabled() {
      return this.nextReportId === null
    },

    isDataLoading() {
      return (
        this.isParameterAlertLoading ||
        this.isAircraftDetailsLoading ||
        this.isParameterDefinitionLoading ||
        this.isReportDataLoading ||
        this.isParameterGraphLoading ||
        this.isFleetGraphLoading ||
        this.isReportTimeLinesDataLoading
      )
    },

    getCurrentGraphGroups() {
      if (this.graphGroupsPerReportType) {
        const currentGroup = this.graphGroupsPerReportType?.find(
          item => item.acmfReportNumber === this.reportType.acmfReportNumber,
        )
        return currentGroup?.graphGroups ? currentGroup.graphGroups : []
      }
      return []
    },

    getAirlineName() {
      return this.aircraftDetails?.find(item => item.title === 'Airline Name')
        ?.value
    },

    getTailNumber() {
      return this.aircraftDetails?.find(item => item.title === 'Tail Number')
        ?.value
    },

    getModel() {
      return this.aircraftDetails?.find(item => item.title === 'Model')?.value
    },

    getPlotsFeatureFlag() {
      return AppFeatureFlags.getReportPlots()
    },
  },

  destroyed() {
    if (!this.pageNavigation) {
      this.setTabIndexFromLocalStorage(0)
    }
  },

  async created() {
    await this.refreshReportData(this.reportId, this.tailNumber)
    if (this.plotTabVisible) {
      this.initPlots()
    } else {
      this.reportsTabIndex = 0
      this.setTabIndexFromLocalStorage(0)
    }
  },

  methods: {
    ...mapActions(ARVC.NAMESPACE, [
      ARVC.GET_RAW_REPORT_TEXT,
      ARVC.GET_PARAMETER_ALERT,
      ARVC.GET_PARAMETER_DEFINITION,
      ARVC.GET_PARAMETER_GRAPH,
      ARVC.GET_SERIES_DATA_GRAPH,
      ARVC.GET_FLEET_COMPARISON_DATA,
      ARVC.GET_EXPORTED_REPORTS_GIVEN_REPORT_ID,
      ARVC.GET_REPORT_PLOT_GROUPS,
      ARVC.GET_REPORT_PLOT,
    ]),

    ...mapActions(ARLC.NAMESPACE, [
      ARLC.GET_ACMF_AIRLINES,
      ARLC.GET_ACMF_MASTER_MODELS,
      ARLC.GET_ACMF_TAILS,
      ARLC.GET_ACMF_SYSTEMS,
      ARLC.GET_ACMF_REPORT_TYPES,
      ARLC.GET_ACMF_PARAMETER_NAMES,
    ]),

    ...mapActions(LC.NAMESPACE, [LC.GET_MODELS]),

    handleExportReport() {
      this[ARVC.GET_EXPORTED_REPORTS_GIVEN_REPORT_ID](this.reportId)
    },

    handleShowRawReport() {
      this.rawReportDialogVisible = true
      this[ARVC.GET_RAW_REPORT_TEXT](this.reportId)
    },

    handleParameterValueClick(event, parameter) {
      this.clickedParameter = parameter
      this.x = event.x
      this.y = event.y
      this.reportMenuDialogVisible = true
    },

    handleParameterAlertDialogClick() {
      this.parameterAlertDialogVisible = true
      this.getParameterAlertData(this.clickedParameter)
    },

    handleReportAlertClick(item) {
      this.parameterAlertDialogVisible = true
      this.getParameterAlertData(item)
    },

    handleParameterDefinitionDialogClick() {
      this.parameterDefinitionDialogVisible = true
      const filters = {
        tailNumber: this.tailNumber,
        parameterId: this.clickedParameterId,
      }
      this[ARVC.GET_PARAMETER_DEFINITION](filters)
    },

    updateParameterGraphData() {
      this.parameterGraphDialogVisible = true
      this.isSeriesData = this.isReportObjectSeriesData()
      if (this.isSeriesData) {
        const seriesData = this.getSeriesDataForGraph()
        const filters = {
          parameterId: this.clickedParameterId,
          tailNumber: this.tailNumber,
          values: seriesData,
          isSeriesData: this.isSeriesData,
        }
        this[ARVC.GET_SERIES_DATA_GRAPH](filters)
      } else {
        const filters = {
          tailNumber: this.tailNumber,
          parameterId: this.clickedParameterId,
          isSeriesData: this.isSeriesData,
          startDate:
            this.parameterGraphDates.dates.value !== '0'
              ? getFormattedAPIDate(this.parameterGraphDates.dates.startDate)
              : getFormattedAPIDate(new Date(1980, 0, 1)),
          endDate:
            this.parameterGraphDates.dates.value !== '0'
              ? getFormattedAPIDate(this.parameterGraphDates.dates.endDate)
              : getFormattedAPIDate(new Date()),
          reportNumber: this.reportId,
          isDataElementId: false,
        }
        this[ARVC.GET_PARAMETER_GRAPH](filters)
      }
    },

    handleParameterGraphDialogClick() {
      this.updateParameterGraphData()
    },

    handleFaultMonitorAlertClick(id) {
      this.faultMonitorAlertDialogVisible = true
      this.getFaultMonitorAlertData(id)
    },

    updateFleetGraphData() {
      this.fleetGraphDialogVisible = true
      const params = {
        parameterId: this.clickedParameterId,
        tailNumber: this.tailNumber,
        startDate: getFormattedAPIDate(this.fleetGraphDates.dates.startDate),
        endDate: getFormattedAPIDate(this.fleetGraphDates.dates.endDate),
      }
      const rowIndex = this.clickedParameter.rowIndex
      this.isSeriesData = this.isReportObjectSeriesData()
      if (this.isSeriesData) {
        params.index = rowIndex
      }
      this[ARVC.GET_FLEET_COMPARISON_DATA](params)
    },

    handleFleetGraphDialogClick() {
      this.updateFleetGraphData()
    },

    handleCloseDialog() {
      this.faultMonitorAlertDialogVisible = false
      this.fleetGraphDialogVisible = false
      this.parameterAlertDialogVisible = false
      this.parameterDefinitionDialogVisible = false
      this.parameterGraphDialogVisible = false
      this.rawReportDialogVisible = false
    },

    handleCloseMenu() {
      this.reportMenuDialogVisible = false
    },

    navToPreviousReport() {
      this.pageNavigation = true
      this.navToReport(this.previousReportId, this.tailNumber)
    },

    navToNextReport() {
      this.pageNavigation = true
      this.navToReport(this.nextReportId, this.tailNumber)
    },

    getFaultMonitorAlertData(id) {
      const faultMonitor = this.getFaultMonitorGivenId(id)
      this.isFaultMonitorAlertLoading = true
      setTimeout(() => {
        this.definition = faultMonitor
        this.isFaultMonitorAlertLoading = false
      }, 500)
    },

    getParameterAlertData(item) {
      const value = item.value ? item.value : item.text
      const params = {
        parameterId: item.parameterId,
        tailNumber: this.tailNumber,
        parameterDetails: { ...item, value },
      }
      this[ARVC.GET_PARAMETER_ALERT](params)
    },

    handleParameterGraphDateChange(dates) {
      this.parameterGraphDates = dates
      this.updateParameterGraphData()
    },

    handleFleetGraphDateChange(dates) {
      this.fleetGraphDates = dates
      this.updateFleetGraphData()
    },

    handleReportTimeLineChange(reportId) {
      this.pageNavigation = true
      this.navToReport(reportId, this.tailNumber)
    },

    handleTabChange(tabIndex) {
      this.reportsTabIndex = tabIndex
      this.setTabIndexFromLocalStorage(tabIndex)
    },

    getTabIndexFromLocalStorage() {
      const tabIndex = sessionStorage.getItem('report_details_tab_index')
      return tabIndex ? Number(tabIndex) : 0
    },

    setTabIndexFromLocalStorage(tabIndex) {
      sessionStorage.setItem('report_details_tab_index', tabIndex)
    },

    getDateRangeGivenDateChip(data) {
      const endDate = this.currentReportDate
        ? this.currentReportDate
        : new Date()
      let range = {}
      switch (data) {
        case 0:
          range.startDate = getStartDateGivenEndDateAndTimeline(endDate, 30)
          range.endDate = getFormattedAPIDate(endDate)
          break
        case 1:
          range.startDate = getStartDateGivenEndDateAndTimeline(endDate, 90)
          range.endDate = getFormattedAPIDate(endDate)
          break
        case 2:
          range.startDate = getStartDateGivenEndDateAndTimeline(endDate, 180)
          range.endDate = getFormattedAPIDate(endDate)
          break
        case 3:
          range.startDate = getStartDateGivenEndDateAndTimeline(endDate, 365)
          range.endDate = getFormattedAPIDate(endDate)
          break
        case 4:
          range.startDate = `${new Date().getFullYear()}-01-01`
          range.endDate = getFormattedAPIDate(new Date())
          break
        default:
          range = null
          break
      }
      return range
    },

    async handleUpdateGraphGroup(data) {
      if (data !== this.graphGroupSelected) {
        this.loadingPagePlots = true
        this.graphGroupSelected = data
        this.chartData = []
        this.pagePlots = []
        this.savedPlots = []
        await this.initPlotData()
        await this.loadPrepopulatedPlots()
        this.loadingPagePlots = false
      }
    },

    async handleUpdateDateRange(data) {
      if (data !== this.dateRangeSelected) {
        this.loadingPagePlots = true
        this.dateRangeSelected = data
        this.chartData = []
        this.pagePlots = []
        this.savedPlots = []
        await this.initPlotData()
        await this.loadPrepopulatedPlots()
        this.loadingPagePlots = false
      }
    },

    async initPlots() {
      this.loadingPagePlots = true
      this.dateRangeSelected = 1
      this.graphGroupSelected = this.graphGroupsPerReportType?.find(
        item => item.acmfReportNumber === this.reportType.acmfReportNumber,
      ).graphGroups[0].graphGroupId
      this.currentReportDate = this.reportTimeLinesData.find(
        item => item.reportId === this.reportId,
      )?.timeStamp
      await this.initPlotFilters()
      await this.initPlotData()
      await this.loadPrepopulatedPlots()
      this.loadingPagePlots = false
    },

    async initPlotFilters() {
      await this.callAcmfFilterLookups()
      await this.initAcmfFilters()
    },

    async callAcmfFilterLookups() {
      await Promise.all([
        this[ARLC.GET_ACMF_AIRLINES](),
        this[ARLC.GET_ACMF_MASTER_MODELS](),
        this[ARLC.GET_ACMF_TAILS](),
        this[ARLC.GET_ACMF_SYSTEMS](),
        this[ARLC.GET_ACMF_REPORT_TYPES](),
        this[ARLC.GET_ACMF_PARAMETER_NAMES](),
        this[LC.GET_MODELS](),
      ])
    },

    async initAcmfFilters() {
      this.filterLookups = []
      this.loadAcmfAirlines()
      this.loadAcmfMasterModels()
      this.loadAcmfTails()
      this.loadAcmfSystems()
      this.loadAcmfReportTypes()
      this.loadAcmfParameterNames()
      this.loadUserModels()
    },

    async initPlotData() {
      const dateRange = this.getDateRangeGivenDateChip(this.dateRangeSelected)
      const params = {
        graphGroupId: this.graphGroupSelected,
        tailNumber: this.getTailNumber,
        ...dateRange,
      }

      await Promise.all([this[ARVC.GET_REPORT_PLOT_GROUPS](params)])
    },

    async loadPrepopulatedPlots() {
      const selections = {
        airlineName: this.getAirlineName,
        tailNumber: this.getTailNumber,
        model: this.getModel,
        reportNumber: this.reportType?.acmfReportNumber,
        reportTypeId: this.reportType?.acmfReportTypeId,
        dateChip: this.dateRangeSelected,
      }

      if (this.reportPlotGraphGroups) {
        const prePopulatedPlots = this.reportPlotGraphGroups.graphs?.map(
          item => {
            const graphParams = {
              ...selections,
              plotTypeId: 8068,
              graphId: item.graphId,
              title: item.graphName,
              parameters: item.acmfDataElements,
            }
            return this.createPrePopulatedReportPlot(graphParams)
          },
        )

        if (prePopulatedPlots) {
          this.savedPlots = [...prePopulatedPlots]

          for (const plot of this.savedPlots) {
            this.savedPlotId = plot.id
            await this.handlePlotPreviewAccept()
          }
        }
      } else {
        this.savedPlots = []
      }
    },

    handleExpand(pagePlotId) {
      this.pagePlotDialogVisible = true
      this.loadingPlotDialogData = true
      setTimeout(() => {
        const pagePlot = this.getPagePlotGivenPlotId(pagePlotId)
        this.expandedPagePlot = pagePlot
        this.loadingPlotDialogData = false
      }, 500)
    },

    async handlePagePlotDialog(plotId) {
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      const pagePlotTitle =
        pagePlot.title.trim().length > 0 ? pagePlot.title.trim() : 'PLOT NAME'
      pagePlot.title = pagePlotTitle
      if (pagePlot) {
        const isDupe = this.isDuplicatePlotName(plotId, pagePlot.title)
        if (isDupe) {
          this.dupeNameDialogVisible = true
          this.pagePlotName = pagePlot.title
        }
        if (!isDupe) {
          this.isPlotSaving = true
          await this.getPagePlotImageSrc(plotId)
          setTimeout(() => {
            this.showSavePlotToast = true
            this.isPlotSaving = false
          }, 3000)
        }
      }
    },

    handleClosePlotDialog() {
      this.pagePlotDialogVisible = false
    },

    updateTabView(plotId, tabIndex, hidden) {
      this.loadingPlotIds.push(plotId)
      setTimeout(() => {
        this.handleUpdateTabViewPagePlot(plotId, tabIndex, hidden)
        this.loadingPlotIds = this.loadingPlotIds.filter(id => id !== plotId)
      }, 1000)
    },

    async handleRefreshData(plotId, plotTypeId, event, options) {
      this.loadingPlotIds.push(plotId)
      if (event === PLOT_UPDATE_MESSAGES.updateDateRange) {
        const pagePlot = this.getPagePlotGivenPlotId(plotId)

        const dateRange = this.getDateRangeGivenDateChip(options)
        const params = {
          graphId: pagePlot.graphId,
          tailNumber: this.getTailNumber,
          ...dateRange,
        }
        await Promise.all([this[ARVC.GET_REPORT_PLOT](params)])
      }
      this.handleManualApplyPagePlot(plotId, plotTypeId, event, options)
      this.loadingPlotIds = this.loadingPlotIds.filter(id => id !== plotId)
    },
  },
}
</script>

<style lang="scss">
.asc-report-details-container {
  background-color: $gray-100;

  &.container {
    padding: 16px;
  }

  .asc-container--contentWrapper {
    .asc-tab__content--primary,
    .asc-tab__content--secondary {
      padding: 0 !important;
      width: 100%;
    }
  }
}
</style>
